import { Avatar } from './Avatar.tsx';

/** Les trois métaux, dans l'ordre du podium. Au-delà, plus de métal : le
 *  quatrième n'est pas un rang, c'est le peloton. */
const METAUX = ['or', 'argent', 'bronze'] as const;

/** Le métal d'un rang : un dégradé, pas un aplat.
 *
 *  Un métal se reconnaît à son REFLET — l'écart entre sa lumière et son ombre
 *  sur une surface courbe. Peint d'une seule teinte, l'or n'est qu'un jaune et
 *  l'argent qu'un gris, indistinguables l'un de l'autre sur un écran en plein
 *  soleil. La bande claire à mi-course fait le reflet, et c'est elle qui les
 *  sépare.
 *
 *  Elle est plus large et plus vive sur l'ARGENT : c'est tout ce qu'il a. L'or
 *  a sa teinte, le bronze sa chaleur ; l'argent n'existe que par son éclat.
 *
 *  Rend `null` au-delà du bronze, à l'appelant de dire ce qu'il fait du
 *  peloton. Les marches d'un podium, elles, ne sont jamais que trois.
 *
 *  La recette est ici et nulle part ailleurs : la pastille du meneur en haut
 *  de l'écran de jeu et la marche qu'il occupe au club-house portent le même
 *  or, sans quoi on croirait à deux distinctions différentes. */
export function degradeMetal(rang: number): string | null {
    const m = METAUX[rang];
    if (!m) return null;
    const eclat = m === 'argent'
        ? '46%, var(--podium-argent-clair) 54%'
        : `48%, var(--podium-${m}-clair) 52%`;
    return `linear-gradient(140deg,
        var(--podium-${m}-clair) 0%,
        var(--podium-${m}) 30%,
        var(--podium-${m}-clair) ${eclat},
        var(--podium-${m}) 70%,
        var(--podium-${m}-sombre) 100%)`;
}

export interface MarchePodium {
    userId: string;
    displayName: string;
    avatarUrl: string | null;
    /** Le score, en grand au-dessus de la marche. */
    valeur: string;
    /** Ce qui précise le score quand il ne suffit pas à expliquer le rang :
     *  la moyenne par trou d'une partie de putting, par exemple, où celui qui
     *  a le plus petit total n'est pas forcément devant. */
    detail?: string | null;
}

/* Le podium : au club-house, pas sur le terrain.
 *
 * ── Pourquoi il n'existe qu'à la fin ────────────────────────────────────────
 *
 * Un classement de mi-parcours n'a rien couronné. Pendant la partie, qui mène
 * se lit sur la pile d'avatars en haut de l'écran — un coup d'œil, pas une
 * cérémonie. Le podium, lui, arrive quand tout est joué : c'est le moment où
 * l'on montre son téléphone aux autres.
 *
 * ── L'ordre 2-1-3 ───────────────────────────────────────────────────────────
 *
 * Le premier au centre, sur la marche la plus haute : la convention de toute
 * estrade de récompense, lue sans légende parce que tout le monde l'a déjà
 * vue. Le rang est en plus ÉCRIT sur la marche — une hauteur se compare, elle
 * ne se lit pas, et il ne faut pas compter les marches pour savoir qui est
 * deuxième.
 *
 * Un podium se dresse à deux ou à trois. Seul, il n'y a rien à classer.
 */
export function Podium({ joueurs, moiId }: {
    /** Déjà classés, du premier au troisième. Au-delà, rien : le peloton se
     *  lit dans les cartes, en dessous. */
    joueurs: MarchePodium[];
    moiId?: string | null;
}) {
    if (joueurs.length < 2) return null;
    const hauteurs = ['h-20', 'h-14', 'h-10'];

    return (
        <div className="flex items-end justify-center gap-3 pb-1 pt-2">
            {[joueurs[1], joueurs[0], joueurs[2]].map((j, i) => {
                if (!j) return <div key={`vide-${i}`} className="w-20" />;
                const rang = joueurs.indexOf(j);
                const cestMoi = j.userId === moiId;
                return (
                    <div key={j.userId} className="flex w-20 flex-col items-center gap-1">
                        <Avatar name={j.displayName} avatarUrl={j.avatarUrl}
                            size={rang === 0 ? 56 : 44} />
                        {/* « vous » plutôt que son propre prénom : on se cherche
                            sur un podium, et un prénom parmi trois prénoms se
                            cherche vraiment. */}
                        <span className={`w-full truncate text-center text-xs ${
                            cestMoi ? 'font-bold' : 'font-semibold opacity-75'}`}>
                            {cestMoi ? 'vous' : j.displayName.split(' ')[0]}
                        </span>
                        <span className="font-score text-xl leading-none">{j.valeur}</span>
                        {j.detail && (
                            <span className="text-[10px] font-semibold leading-none opacity-60">
                                {j.detail}
                            </span>
                        )}
                        <div className={`flex w-full items-start justify-center rounded-t-field pt-1 ${hauteurs[rang]}`}
                            style={{ background: degradeMetal(rang) ?? undefined }}>
                            {/* Le chiffre du rang en NOIR translucide, et non
                                dans l'ombre du métal : gravé de sa propre
                                teinte, il disparaîtrait sur le bronze, le plus
                                sombre des trois. Le noir tient sur les trois. */}
                            <span className="font-score text-2xl leading-none"
                                style={{ color: 'rgb(0 0 0 / 0.6)' }}>
                                {rang + 1}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
